/** @file One REST request (same-origin cookies, JSON body); every failure leaves as a Pose2DError. */
import { throwError } from '@pose2d/format/throwError';
import { toPose2DError } from '@pose2d/format/toPose2DError';

/** The part of `fetch` used (same shape as createApiClient's FetchLike). */
type Fetcher = (url: string, init: RequestInit) => Promise<Response>;
type Method = 'GET' | 'POST' | 'PUT';

/** The one shape for every failure that is not a valid Pose2D error body; `hint` names the cause. */
function unexpected(request: string, hint: string): never {
  return throwError('NOT_FOUND', { kind: 'route', ref: request }, { hint });
}

async function fetchResponse(fetchFn: Fetcher, request: string, path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetchFn(path, init);
  } catch (e) {
    return unexpected(request, `network error: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function readJson(res: Response, request: string): Promise<unknown> {
  if (!(res.headers.get('content-type') ?? '').includes('application/json')) return null;
  try {
    return (await res.json()) as unknown;
  } catch {
    return unexpected(request, `invalid JSON body (status ${res.status})`);
  }
}

function errorOf(body: unknown): unknown {
  return typeof body === 'object' && body !== null && 'error' in body ? body.error : undefined;
}

function fail(request: string, status: number, body: unknown): never {
  if (body === null) return unexpected(request, `HTTP ${status} without a JSON body; is the worker (pnpm --filter @pose2d/worker dev) running?`);
  const error = toPose2DError({ pose2d: errorOf(body) });
  if (error === null) return unexpected(request, `unexpected error body (status ${status})`);
  throw Object.assign(new Error(error.message), { pose2d: error });
}

/** `undefined` for 204, else the JSON body of a 2xx; anything else throws (NOT_FOUND kind route with a hint). */
export async function requestApi(fetchFn: Fetcher, method: Method, path: string, body?: unknown): Promise<unknown> {
  const init: RequestInit =
    body === undefined
      ? { method, credentials: 'same-origin' }
      : { method, credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) };
  const request = `${method} ${path}`;
  const res = await fetchResponse(fetchFn, request, path, init);
  if (res.status === 204) return undefined;
  const json = await readJson(res, request);
  if (res.ok && json !== null) return json;
  return fail(request, res.status, json);
}
